import {
     Alert,
     Badge,
     Button,
     Card,
     Center,
     Container,
     Divider,
     Group,
     List,
     Modal,
     NumberInput,
     Paper,
     Stack,
     Text,
     Textarea,
     ThemeIcon,
     Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
     IconAlertCircle,
     IconArrowRight,
     IconCalendar,
     IconCheck,
     IconCurrencyNaira,
     IconFileText,
     IconTrendingUp,
     IconWallet,
     IconBuildingStore,
     IconCash,
     IconCoins,
     IconCreditCard,
     IconPigMoney,
     IconBusinessplan,
     IconHome,
     IconSchool,
     IconBriefcase,
     IconCurrencyDollar,
     IconCamera,
     IconShieldCheck,
     IconX,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppName, HttpCodes } from "../../lib/data";
import { AppContext } from "../../contexts/AppContext";
import { IAvailableLoanPlansProps, ILoanRequestProps } from "../../types/Loan";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";
import { useToastNotification } from "../../hooks/useToastNotification";
import { PaginatedResponse, NonPaginatedResponse } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import {
     isBvnComplete,
     isBvnImageVerified,
} from "../../lib/utils/checkBvnComplete";
import { IKycVerificatitonProps } from "../../types/Kyc";
import Loader from "../../components/Loader";

const formatLabel = (value: string) =>
     value
          .split(/[_\s]/)
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

const iconMap: Record<string, any> = {
     IconWallet,
     IconBuildingStore,
     IconTrendingUp,
     IconCash,
     IconCoins,
     IconCreditCard,
     IconPigMoney,
     IconBusinessplan,
     IconHome,
     IconSchool,
     IconBriefcase,
     IconCurrencyDollar,
};

const getIconComponent = (iconName: string) => {
     return iconMap[iconName] || IconWallet;
};

export default function RequestLoan() {
     const { user, loading } = useAuth();
     const navigate = useNavigate();
     const [applicationModalOpened, setApplicationModalOpened] =
          useState(false);
     const [confirmationModalOpened, setConfirmationModalOpened] =
          useState(false);
     const [pendingSubmissionData, setPendingSubmissionData] = useState<{
          amount: number;
          duration: number;
          purpose: string;
     } | null>(null);
     const [selectedLoanData, setSelectedLoanData] =
          useState<IAvailableLoanPlansProps | null>(null);
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [pendingLoansCount, setPendingLoansCount] = useState(0);

     const loanOptions = useContext(AppContext);
     const toast = useToastNotification();

     const { data: loanRequests, reloadData: reloadLoanRequests } =
          usePerformRequest<PaginatedResponse<ILoanRequestProps[]>>({
               method: Endpoints.GetLoanHistory(1, 100).method,
               route: Endpoints.GetLoanHistory(1, 100).route,
          });

     const { data: kycStatusResponse } = usePerformRequest<
          NonPaginatedResponse<IKycVerificatitonProps>
     >({
          method: Endpoints.GetKycStatus().method,
          route: Endpoints.GetKycStatus().route,
     });

     const kycStatus = kycStatusResponse?.data;

     useEffect(() => {
          if (loanRequests?.data) {
               const pendingCount = loanRequests.data.filter(
                    (loan) => loan.status === "pending"
               ).length;
               setPendingLoansCount(pendingCount);
          }
     }, [loanRequests]);

     const formatCurrency = (amount: number) => {
          return `₦${amount.toLocaleString("en-NG")}`;
     };

     // Check if KYC is completely verified (all 4 steps must be approved)
     const isKycComplete =
          kycStatus?.identityStatus === "approved" &&
          kycStatus?.addressStatus === "approved" &&
          kycStatus?.employmentStatus === "approved" &&
          kycStatus?.financialDocumentStatus === "approved";

     // const canApplyForLoan = pendingLoansCount <= 2 && isBvnComplete(user) && isKycComplete;

     const form = useForm({
          initialValues: {
               amount: 0,
               duration: 1,
               purpose: "",
          },
          validate: {
               amount: (value) => {
                    if (!selectedLoanData) return null;
                    if (!value || value <= 0) return "Amount is required";
                    if (value < selectedLoanData.minimumAmount) {
                         return `Minimum amount is ${formatCurrency(
                              selectedLoanData.minimumAmount
                         )}`;
                    }
                    if (value > selectedLoanData.maximumAmount) {
                         return `Maximum amount is ${formatCurrency(
                              selectedLoanData.maximumAmount
                         )}`;
                    }
                    return null;
               },
               duration: (value) => {
                    if (!selectedLoanData) return null;
                    if (!value || value <= 0) return "Duration is required";
                    if (value < selectedLoanData.minimumPeriod) {
                         return `Minimum duration is ${selectedLoanData.minimumPeriod} months`;
                    }
                    if (value > selectedLoanData.maximumPeriod) {
                         return `Maximum duration is ${selectedLoanData.maximumPeriod} months`;
                    }
                    return null;
               },
               purpose: (value) => {
                    if (!value || value.trim().length === 0)
                         return "Purpose is required";
                    if (value.trim().length < 10)
                         return "Purpose must be at least 10 characters";
                    return null;
               },
          },
     });

     if (loading) {
          return (
               <Container size="xl" py="xl">
                    <Paper
                         shadow="sm"
                         p="xl"
                         radius="sm"
                         style={{
                              background: "white",
                              border: "2px solid #e5e7eb",
                         }}
                    >
                         <Stack align="center" gap="lg" py="xl">
                              <Loader size="xl" />
                              <Text size="lg" c="dimmed">
                                   Loading your dashboard...
                              </Text>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     const handleApplyClick = () => {
          if (!selectedLoanData) return;

          // Check if BVN is complete
          if (!isBvnComplete(user)) {
               toast.error(
                    "Please complete your BVN profile verification before requesting a loan."
               );
               navigate("/dashboard/profile");
               setApplicationModalOpened(false);
               return;
          }

          // Check if KYC is completely verified
          if (!isKycComplete) {
               toast.error(
                    "Please complete and get approval for all KYC documents before requesting a loan. You can only view loans until KYC is fully approved."
               );
               navigate("/dashboard/kyc");
               setApplicationModalOpened(false);
               return;
          }

          // Check if user has more than 2 pending loans
          if (pendingLoansCount > 2) {
               toast.error(
                    `You have ${pendingLoansCount} pending loan requests. Please wait for them to be processed before applying for a new loan.`
               );
               setApplicationModalOpened(false);
               return;
          }

          // Reset form with defaults based on selected loan
          form.reset();
          form.setValues({
               amount: selectedLoanData.minimumAmount,
               duration: selectedLoanData.minimumPeriod,
               purpose: "",
          });

          setApplicationModalOpened(true);
     };

     const handleSubmitLoanApplication = async (values: typeof form.values) => {
          if (!selectedLoanData) {
               toast.error("Please select a loan plan first");
               return;
          }

          // Calculate total payable
          const principal = values.amount;
          const interestRate = selectedLoanData.interestRate || 0;
          const duration = values.duration;
          const interestAmount = (principal * interestRate * duration) / 100;
          const totalPayable = principal + interestAmount;

          // Store pending submission data and show confirmation modal
          setPendingSubmissionData({
               amount: values.amount,
               duration: values.duration,
               purpose: values.purpose,
          });
          setConfirmationModalOpened(true);
     };

     const handleConfirmLoanApplication = async () => {
          if (!selectedLoanData || !pendingSubmissionData) {
               toast.error("Please select a loan plan first");
               return;
          }

          try {
               setIsSubmitting(true);
               setConfirmationModalOpened(false);

               const r = await PerformRequest({
                    route: Endpoints.GetApplyForLoan().route,
                    method: Endpoints.GetApplyForLoan().method,
                    data: {
                         amount: pendingSubmissionData.amount,
                         duration: pendingSubmissionData.duration,
                         purpose: pendingSubmissionData.purpose,
                         loanID: selectedLoanData.id,
                    },
               });

               if (r) {
                    if (r.status === HttpCodes.Created) {
                         toast.success(
                              "Your loan application has been submitted successfully"
                         );
                         setApplicationModalOpened(false);
                         setConfirmationModalOpened(false);
                         form.reset();
                         setPendingSubmissionData(null);
                         // Reload loan requests to update pending count
                         reloadLoanRequests();
                    } else {
                         toast.error(
                              r?.message || "Failed to submit loan application"
                         );
                    }
               }
          } catch (error) {
               toast.error(
                    "An error occurred while submitting your application"
               );
          } finally {
               setIsSubmitting(false);
          }
     };

     // Show BVN completion requirement if BVN is not complete
     if (user && !isBvnComplete(user)) {
          return (
               <Container size="xl" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background: "white",
                              border: "2px solid #f59e0b",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="yellow"
                                   variant="light"
                              >
                                   <IconAlertCircle size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#92400e" }}
                                   >
                                        BVN Verification Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="xl">
                                        You must complete your BVN profile
                                        verification before you can request a
                                        loan. This helps us verify your identity
                                        and process your loan application
                                        securely.
                                   </Text>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/profile")
                                        }
                                        style={{
                                             background: "#679369",
                                        }}
                                        leftSection={<IconWallet size={20} />}
                                   >
                                        Complete BVN Verification
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     // Show BVN image verification requirement if BVN image is not verified
     if (user && isBvnComplete(user) && !isBvnImageVerified(user)) {
          return (
               <Container size="xl" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background:
                                   "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                              border: "2px solid #fcd34d",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="yellow"
                                   variant="light"
                              >
                                   <IconAlertCircle size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#78350f" }}
                                   >
                                        BVN Image Verification Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="md">
                                        You must complete your BVN image
                                        verification by capturing a selfie that
                                        matches your BVN photo before you can
                                        request a loan.
                                   </Text>
                                   <Alert
                                        color="yellow"
                                        radius="md"
                                        mb="xl"
                                        style={{
                                             border: "1px solid #fcd34d",
                                             background: "white",
                                             textAlign: "left",
                                        }}
                                   >
                                        <Group gap={8} mb="xs">
                                             <IconCamera
                                                  size={16}
                                                  style={{ color: "#78350f" }}
                                             />
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  style={{ color: "#78350f" }}
                                             >
                                                  Selfie Requirements:
                                             </Text>
                                        </Group>
                                        <Stack gap={4}>
                                             <Group gap={8}>
                                                  <IconCheck
                                                       size={14}
                                                       style={{
                                                            color: "#679369",
                                                       }}
                                                  />
                                                  <Text size="sm">
                                                       Your face is clearly
                                                       visible and well-lit
                                                  </Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck
                                                       size={14}
                                                       style={{
                                                            color: "#679369",
                                                       }}
                                                  />
                                                  <Text size="sm">
                                                       No caps, hats, or head
                                                       coverings
                                                  </Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck
                                                       size={14}
                                                       style={{
                                                            color: "#679369",
                                                       }}
                                                  />
                                                  <Text size="sm">
                                                       No glasses
                                                  </Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck
                                                       size={14}
                                                       style={{
                                                            color: "#679369",
                                                       }}
                                                  />
                                                  <Text size="sm">
                                                       Good lighting conditions
                                                  </Text>
                                             </Group>
                                        </Stack>
                                   </Alert>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/profile")
                                        }
                                        style={{
                                             background:
                                                  "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                             fontWeight: 600,
                                        }}
                                        leftSection={<IconWallet size={20} />}
                                   >
                                        Complete BVN Image Verification
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     // Show KYC completion requirement if KYC is not completely verified
     if (
          user &&
          isBvnComplete(user) &&
          isBvnImageVerified(user) &&
          kycStatus &&
          !isKycComplete
     ) {
          return (
               <Container size="xl" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background: "white",
                              border: "2px solid #f59e0b",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="yellow"
                                   variant="light"
                              >
                                   <IconAlertCircle size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#92400e" }}
                                   >
                                        KYC Verification Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="xl">
                                        You must complete and get approval for
                                        all KYC documents before you can apply
                                        for loans. Until your KYC is fully
                                        approved, you can only view available
                                        loan options.
                                   </Text>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/kyc")
                                        }
                                        style={{
                                             background: "#679369",
                                        }}
                                        leftSection={<IconFileText size={20} />}
                                   >
                                        Complete KYC Verification
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     return (
          <Container size="xl" py="xl" className="request-loan-container">
               <Stack gap="xl">
                    {/* Loan repayment UI moved to /dashboard/loan-repayment */}

                    {/* Pending Loans Warning */}
                    {pendingLoansCount > 2 && (
                         <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                         >
                              <Paper
                                   p="lg"
                                   radius="sm"
                                   style={{
                                        background:
                                             "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                        border: "2px solid #f59e0b",
                                   }}
                              >
                                   <Group gap="md" align="center">
                                        <ThemeIcon
                                             size="lg"
                                             radius="sm"
                                             color="yellow"
                                        >
                                             <IconAlertCircle size={24} />
                                        </ThemeIcon>
                                        <div style={{ flex: 1 }}>
                                             <Text
                                                  size="md"
                                                  fw={600}
                                                  style={{
                                                       color: "#92400e",
                                                       marginBottom: "0.25rem",
                                                  }}
                                             >
                                                  Application Limit Reached
                                             </Text>
                                             <Text
                                                  size="sm"
                                                  style={{ color: "#78350f" }}
                                             >
                                                  You currently have{" "}
                                                  {pendingLoansCount} pending
                                                  loan requests. Please wait for
                                                  them to be processed before
                                                  submitting a new application.
                                             </Text>
                                        </div>
                                   </Group>
                              </Paper>
                         </motion.div>
                    )}

                    {/* Header Section */}
                    <motion.div
                         initial={{ opacity: 0, y: -20 }}
                         animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.5 }}
                         className="text-center mb-8"
                    >
                         <Title
                              order={1}
                              size="3rem"
                              fw={800}
                              className="mb-4"
                              style={{ color: "#679369" }}
                         >
                              Choose Your Loan Option
                         </Title>

                         <Center>
                              <Text
                                   ta="center"
                                   size="xl"
                                   c="dimmed"
                                   className="max-w-2xl mx-auto"
                              >
                                   Select the loan product that best fits your
                                   needs. Compare options and find the perfect
                                   solution for you.
                              </Text>
                         </Center>
                    </motion.div>

                    {/* Loan Options Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                         {loanOptions?.availableLoanPlans.map((loan, index) => {
                              return (
                                   <motion.div
                                        key={loan.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                             duration: 0.5,
                                             delay: index * 0.2,
                                        }}
                                   >
                                        <Card
                                             shadow="xl"
                                             padding="xl"
                                             radius="lg"
                                             className="h-full transition-all duration-300 hover:shadow-2xl"
                                             style={{
                                                  background: "white",
                                                  border: "2px solid",
                                                  borderColor: "#E0E0E0",
                                             }}
                                        >
                                             <Stack gap="lg">
                                                  <ThemeIcon
                                                       size={160}
                                                       radius="md"
                                                       variant="light"
                                                       color="green"
                                                       style={{
                                                            alignSelf: "center",
                                                       }}
                                                  >
                                                       {(() => {
                                                            const IconComponent =
                                                                 getIconComponent(
                                                                      loan.iconName ||
                                                                           "IconWallet"
                                                                 );
                                                            return (
                                                                 <IconComponent
                                                                      size={64}
                                                                 />
                                                            );
                                                       })()}
                                                  </ThemeIcon>

                                                  <Stack gap="md">
                                                       <Title
                                                            order={3}
                                                            size="h2"
                                                            fw={700}
                                                            style={{
                                                                 color: "#679369",
                                                                 margin: 0,
                                                            }}
                                                       >
                                                            {loan.name}
                                                       </Title>

                                                       <Group
                                                            gap="xs"
                                                            wrap="wrap"
                                                       >
                                                            <Badge
                                                                 color={
                                                                      loan.allowMultiTenor
                                                                           ? "teal"
                                                                           : "gray"
                                                                 }
                                                                 variant="outline"
                                                            >
                                                                 {loan.allowMultiTenor
                                                                      ? "Multi-tenor"
                                                                      : `Min tenor ${loan.minimumPeriod}m`}
                                                            </Badge>
                                                       </Group>

                                                       <Badge
                                                            size="lg"
                                                            variant="light"
                                                            style={{
                                                                 backgroundColor:
                                                                      "#F5F5F5",
                                                                 color: "#679369",
                                                                 width: "fit-content",
                                                            }}
                                                       >
                                                            {loan.interestRate}%
                                                            per month
                                                       </Badge>

                                                       <Text
                                                            size="sm"
                                                            style={{
                                                                 color: "#666",
                                                            }}
                                                       >
                                                            {loan.description}
                                                       </Text>
                                                  </Stack>

                                                  <Group
                                                       justify="space-between"
                                                       align="start"
                                                  >
                                                       <Stack gap="xs">
                                                            <Text
                                                                 size="xs"
                                                                 c="dimmed"
                                                            >
                                                                 Amount Range
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {formatCurrency(
                                                                      loan.minimumAmount
                                                                 )}{" "}
                                                                 -{" "}
                                                                 {formatCurrency(
                                                                      loan.maximumAmount
                                                                 )}
                                                            </Text>
                                                       </Stack>
                                                       <Stack
                                                            gap="xs"
                                                            align="flex-end"
                                                       >
                                                            <Text
                                                                 size="xs"
                                                                 c="dimmed"
                                                            >
                                                                 Tenor
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      loan.minimumPeriod
                                                                 }{" "}
                                                                 -{" "}
                                                                 {
                                                                      loan.maximumPeriod
                                                                 }{" "}
                                                                 months
                                                            </Text>
                                                       </Stack>
                                                  </Group>

                                                  <Stack gap="xs">
                                                       <Text size="sm" fw={600}>
                                                            Repayment Details
                                                       </Text>
                                                       <Text
                                                            size="sm"
                                                            c="dimmed"
                                                       >
                                                            Frequency:{" "}
                                                            {formatLabel(
                                                                 loan.repaymentFrequency
                                                            )}{" "}
                                                            • Model:{" "}
                                                            {formatLabel(
                                                                 loan.repaymentModel
                                                            )}
                                                       </Text>
                                                       <Group
                                                            gap="xs"
                                                            wrap="wrap"
                                                       >
                                                            {loan.paymentApplicationSequence.map(
                                                                 (seq) => (
                                                                      <Badge
                                                                           key={
                                                                                seq
                                                                           }
                                                                           size="xs"
                                                                           variant="outline"
                                                                           color="gray"
                                                                      >
                                                                           {formatLabel(
                                                                                seq
                                                                           )}
                                                                      </Badge>
                                                                 )
                                                            )}
                                                       </Group>
                                                  </Stack>

                                                  <Stack gap="xs">
                                                       <Text size="sm" fw={600}>
                                                            Fees
                                                       </Text>
                                                       <Text
                                                            size="sm"
                                                            c="dimmed"
                                                       >
                                                            Processing and other
                                                            fees will be
                                                            calculated and
                                                            displayed during the
                                                            loan application
                                                            process.
                                                       </Text>
                                                  </Stack>

                                                  <Button
                                                       fullWidth
                                                       size="md"
                                                       style={{
                                                            background:
                                                                 "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                                            fontWeight: 600,
                                                       }}
                                                       onClick={(e) => {
                                                            setSelectedLoanData(
                                                                 loan
                                                            );
                                                            setApplicationModalOpened(
                                                                 true
                                                            );
                                                       }}
                                                       disabled={
                                                            !isKycComplete ||
                                                            pendingLoansCount >
                                                                 2
                                                       }
                                                  >
                                                       Apply for Loan
                                                  </Button>
                                             </Stack>
                                        </Card>
                                   </motion.div>
                              );
                         })}
                    </div>

                    {/* Info Banner */}
                    <motion.div
                         initial={{ opacity: 0 }}
                         animate={{ opacity: 1 }}
                         transition={{ delay: 0.6, duration: 0.5 }}
                    >
                         <Paper
                              p="xl"
                              radius="lg"
                              style={{
                                   background:
                                        "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                   color: "white",
                              }}
                         >
                              <Group gap="xl" align="flex-start">
                                   <ThemeIcon
                                        size={56}
                                        radius="xl"
                                        style={{
                                             background:
                                                  "rgba(255, 255, 255, 0.15)",
                                             flexShrink: 0,
                                        }}
                                   >
                                        <IconTrendingUp size={28} />
                                   </ThemeIcon>
                                   <div>
                                        <Title
                                             order={4}
                                             mb="xs"
                                             style={{ color: "white" }}
                                        >
                                             Why Choose {AppName} Loans?
                                        </Title>
                                        <Text
                                             size="sm"
                                             style={{
                                                  color: "rgba(255, 255, 255, 0.9)",
                                             }}
                                        >
                                             Our loan products are designed with
                                             your financial well-being in mind.
                                             We offer competitive rates,
                                             flexible terms, and a transparent
                                             process to help you achieve your
                                             financial goals. Apply today and
                                             experience the difference.
                                        </Text>
                                   </div>
                              </Group>
                         </Paper>
                    </motion.div>
               </Stack>

               {/* Loan Application Modal */}
               <Modal
                    opened={applicationModalOpened}
                    onClose={() => setApplicationModalOpened(false)}
                    title={
                         <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="green">
                                   <IconFileText size={20} />
                              </ThemeIcon>
                              <div>
                                   <Text fw={600} size="lg">
                                        Apply for {selectedLoanData?.name}
                                   </Text>
                                   <Text size="xs" c="dimmed">
                                        Fill in the details below to submit your
                                        loan application
                                   </Text>
                              </div>
                         </Group>
                    }
                    centered
                    size="lg"
                    padding="xl"
                    radius="md"
                    styles={{
                         title: {
                              width: "100%",
                         },
                    }}
               >
                    <form onSubmit={form.onSubmit(handleSubmitLoanApplication)}>
                         <Stack gap="lg">
                              {/* Loan Summary */}
                              {selectedLoanData && (
                                   <Paper
                                        p="md"
                                        radius="md"
                                        style={{
                                             background:
                                                  "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                                             border: "1px solid #699269",
                                        }}
                                   >
                                        <Group justify="space-between" mb="xs">
                                             <Text
                                                  size="sm"
                                                  fw={500}
                                                  c="#558B2F"
                                             >
                                                  Interest Rate
                                             </Text>
                                             <Badge
                                                  size="lg"
                                                  variant="filled"
                                                  style={{
                                                       background: "#699269",
                                                  }}
                                             >
                                                  {
                                                       selectedLoanData.interestRate
                                                  }
                                                  % per month
                                             </Badge>
                                        </Group>
                                        <Group justify="space-between">
                                             <Text
                                                  size="sm"
                                                  fw={500}
                                                  c="#558B2F"
                                             >
                                                  Available Range
                                             </Text>
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  c="#558B2F"
                                             >
                                                  {formatCurrency(
                                                       selectedLoanData.minimumAmount
                                                  )}{" "}
                                                  -{" "}
                                                  {formatCurrency(
                                                       selectedLoanData.maximumAmount
                                                  )}
                                             </Text>
                                        </Group>
                                        <Group justify="space-between" mt="xs">
                                             <Text
                                                  size="sm"
                                                  fw={500}
                                                  c="#558B2F"
                                             >
                                                  Maximum Duration
                                             </Text>
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  c="#558B2F"
                                             >
                                                  {
                                                       selectedLoanData.maximumPeriod
                                                  }{" "}
                                                  months
                                             </Text>
                                        </Group>
                                        <Group justify="space-between" mt="xs">
                                             <Text
                                                  size="sm"
                                                  fw={500}
                                                  c="#558B2F"
                                             >
                                                  Minimum Interest Amount
                                             </Text>
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  c="#558B2F"
                                             >
                                                  {formatCurrency(
                                                       selectedLoanData.minimumInterestAmount ??
                                                            0
                                                  )}
                                             </Text>
                                        </Group>
                                        <Group justify="space-between" mt="xs">
                                             <Text
                                                  size="sm"
                                                  fw={500}
                                                  c="#558B2F"
                                             >
                                                  Repayment
                                             </Text>
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  c="#558B2F"
                                                  ta="right"
                                             >
                                                  {formatLabel(
                                                       selectedLoanData.repaymentFrequency
                                                  )}{" "}
                                                  •{" "}
                                                  {formatLabel(
                                                       selectedLoanData.repaymentModel
                                                  )}
                                             </Text>
                                        </Group>
                                        <Group gap="xs" mt="xs" wrap="wrap">
                                             {selectedLoanData.paymentApplicationSequence.map(
                                                  (seq) => (
                                                       <Badge
                                                            key={seq}
                                                            size="xs"
                                                            variant="outline"
                                                            color="green"
                                                       >
                                                            {formatLabel(seq)}
                                                       </Badge>
                                                  )
                                             )}
                                        </Group>
                                   </Paper>
                              )}

                              <Divider />

                              {/* Amount Input */}
                              <NumberInput
                                   label="Loan Amount"
                                   placeholder="Enter amount"
                                   description={
                                        selectedLoanData
                                             ? `Between ${formatCurrency(
                                                    selectedLoanData.minimumAmount
                                               )} and ${formatCurrency(
                                                    selectedLoanData.maximumAmount
                                               )}`
                                             : ""
                                   }
                                   leftSection={<IconCurrencyNaira size={18} />}
                                   hideControls
                                   thousandSeparator=","
                                   min={selectedLoanData?.minimumAmount || 0}
                                   max={selectedLoanData?.maximumAmount || 0}
                                   required
                                   size="md"
                                   styles={{
                                        input: {
                                             fontSize: "16px",
                                             fontWeight: 500,
                                        },
                                   }}
                                   {...form.getInputProps("amount")}
                              />

                              {/* Duration Input */}
                              <NumberInput
                                   label="Loan Duration (Months)"
                                   placeholder="Enter duration in months"
                                   description={
                                        selectedLoanData
                                             ? `Between ${selectedLoanData.minimumPeriod} and ${selectedLoanData.maximumPeriod} months`
                                             : ""
                                   }
                                   leftSection={<IconCalendar size={18} />}
                                   min={selectedLoanData?.minimumPeriod || 1}
                                   max={selectedLoanData?.maximumPeriod || 12}
                                   required
                                   size="md"
                                   styles={{
                                        input: {
                                             fontSize: "16px",
                                             fontWeight: 500,
                                        },
                                   }}
                                   {...form.getInputProps("duration")}
                              />

                              {/* Purpose Input */}
                              <Textarea
                                   label="Purpose of Loan"
                                   placeholder="Describe why you need this loan (minimum 10 characters)"
                                   description="Please provide a clear explanation of how you plan to use the loan"
                                   required
                                   minRows={4}
                                   maxRows={6}
                                   size="md"
                                   styles={{
                                        input: {
                                             fontSize: "15px",
                                        },
                                   }}
                                   {...form.getInputProps("purpose")}
                              />

                              {/* Calculation Summary */}
                              {selectedLoanData &&
                                   form.values.amount > 0 &&
                                   form.values.duration > 0 && (
                                        <Paper
                                             p="md"
                                             radius="md"
                                             style={{
                                                  background: "#f9fafb",
                                                  border: "1px solid #e5e7eb",
                                             }}
                                        >
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  mb="sm"
                                                  c="dimmed"
                                             >
                                                  Loan Summary
                                             </Text>
                                             <Stack gap="xs">
                                                  <Group justify="space-between">
                                                       <Text size="sm">
                                                            Principal Amount:
                                                       </Text>
                                                       <Text size="sm" fw={600}>
                                                            {formatCurrency(
                                                                 form.values
                                                                      .amount
                                                            )}
                                                       </Text>
                                                  </Group>
                                                  <Group justify="space-between">
                                                       <Text size="sm">
                                                            Duration:
                                                       </Text>
                                                       <Text size="sm" fw={600}>
                                                            {
                                                                 form.values
                                                                      .duration
                                                            }{" "}
                                                            month
                                                            {form.values
                                                                 .duration > 1
                                                                 ? "s"
                                                                 : ""}
                                                       </Text>
                                                  </Group>
                                                  <Group justify="space-between">
                                                       <Text size="sm">
                                                            Monthly Interest
                                                            Rate:
                                                       </Text>
                                                       <Text size="sm" fw={600}>
                                                            {
                                                                 selectedLoanData.interestRate
                                                            }
                                                            %
                                                       </Text>
                                                  </Group>
                                                  <Divider my="xs" />
                                                  <Group justify="space-between">
                                                       <Text
                                                            size="sm"
                                                            fw={700}
                                                            c="#699269"
                                                       >
                                                            Estimated Monthly
                                                            Payment:
                                                       </Text>
                                                       <Text
                                                            size="md"
                                                            fw={700}
                                                            c="#699269"
                                                       >
                                                            {formatCurrency(
                                                                 Math.round(
                                                                      (form
                                                                           .values
                                                                           .amount +
                                                                           (form
                                                                                .values
                                                                                .amount *
                                                                                selectedLoanData.interestRate *
                                                                                form
                                                                                     .values
                                                                                     .duration) /
                                                                                100) /
                                                                           form
                                                                                .values
                                                                                .duration
                                                                 )
                                                            )}
                                                       </Text>
                                                  </Group>
                                                  <Group justify="space-between">
                                                       <Text size="sm" fw={700}>
                                                            Total Amount
                                                            Payable:
                                                       </Text>
                                                       <Text size="md" fw={700}>
                                                            {formatCurrency(
                                                                 Math.round(
                                                                      form
                                                                           .values
                                                                           .amount +
                                                                           (form
                                                                                .values
                                                                                .amount *
                                                                                selectedLoanData.interestRate *
                                                                                form
                                                                                     .values
                                                                                     .duration) /
                                                                                100
                                                                 )
                                                            )}
                                                       </Text>
                                                  </Group>
                                             </Stack>
                                        </Paper>
                                   )}

                              {/* Action Buttons */}
                              <Group justify="flex-end" mt="md">
                                   <Button
                                        variant="subtle"
                                        color="gray"
                                        onClick={() =>
                                             setApplicationModalOpened(false)
                                        }
                                        disabled={isSubmitting}
                                   >
                                        Cancel
                                   </Button>
                                   <Button
                                        type="submit"
                                        loading={isSubmitting}
                                        leftSection={<IconCheck size={18} />}
                                        size="md"
                                        style={{
                                             background: "#699269",
                                        }}
                                   >
                                        Submit Application
                                   </Button>
                              </Group>
                         </Stack>
                    </form>
               </Modal>

               {/* Confirmation Modal */}
               <Modal
                    opened={confirmationModalOpened}
                    onClose={() => {
                         setConfirmationModalOpened(false);
                         setPendingSubmissionData(null);
                    }}
                    title={
                         <Group gap="sm">
                              <ThemeIcon
                                   size="lg"
                                   radius="md"
                                   style={{ background: "#F59E0B" }}
                              >
                                   <IconShieldCheck size={20} />
                              </ThemeIcon>
                              <div>
                                   <Text
                                        fw={700}
                                        size="lg"
                                        style={{ color: "#92400E" }}
                                   >
                                        Confirm Loan Application
                                   </Text>
                                   <Text size="xs" c="dimmed">
                                        Please review your loan details before
                                        submitting
                                   </Text>
                              </div>
                         </Group>
                    }
                    centered
                    size="md"
                    padding="xl"
                    radius="md"
                    styles={{
                         content: {
                              background:
                                   "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
                         },
                         header: {
                              borderBottom: "2px solid #e5e7eb",
                              paddingBottom: "1rem",
                         },
                    }}
               >
                    {pendingSubmissionData && selectedLoanData && (
                         <Stack gap="lg">
                              <motion.div
                                   initial={{ opacity: 0, y: -10 }}
                                   animate={{ opacity: 1, y: 0 }}
                                   transition={{ duration: 0.3 }}
                              >
                                   <Paper
                                        p="lg"
                                        radius="md"
                                        style={{
                                             background:
                                                  "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                                             border: "2px solid #3B82F6",
                                        }}
                                   >
                                        <Group gap="sm" mb="md">
                                             <ThemeIcon
                                                  size="lg"
                                                  radius="md"
                                                  style={{
                                                       background: "#3B82F6",
                                                  }}
                                             >
                                                  <IconWallet size={24} />
                                             </ThemeIcon>
                                             <div>
                                                  <Text
                                                       size="sm"
                                                       fw={600}
                                                       style={{
                                                            color: "#3B82F6",
                                                       }}
                                                  >
                                                       Loan Details
                                                  </Text>
                                                  <Text size="xs" c="dimmed">
                                                       Review before confirming
                                                  </Text>
                                             </div>
                                        </Group>

                                        <Stack gap="md">
                                             <Paper
                                                  p="md"
                                                  radius="md"
                                                  style={{
                                                       background: "white",
                                                       border: "1px solid #e5e7eb",
                                                  }}
                                             >
                                                  <Stack gap="sm">
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Loan Product
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      selectedLoanData.name
                                                                 }
                                                            </Text>
                                                       </Group>
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Loan Amount
                                                            </Text>
                                                            <Text
                                                                 size="md"
                                                                 fw={700}
                                                                 style={{
                                                                      color: "#3B82F6",
                                                                 }}
                                                            >
                                                                 {formatCurrency(
                                                                      pendingSubmissionData.amount
                                                                 )}
                                                            </Text>
                                                       </Group>
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Duration
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      pendingSubmissionData.duration
                                                                 }{" "}
                                                                 {pendingSubmissionData.duration ===
                                                                 1
                                                                      ? "month"
                                                                      : "months"}
                                                            </Text>
                                                       </Group>
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Interest Rate
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      selectedLoanData.interestRate
                                                                 }
                                                                 % per month
                                                            </Text>
                                                       </Group>
                                                       <Divider />
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Interest Amount
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {formatCurrency(
                                                                      (pendingSubmissionData.amount *
                                                                           selectedLoanData.interestRate *
                                                                           pendingSubmissionData.duration) /
                                                                           100
                                                                 )}
                                                            </Text>
                                                       </Group>
                                                       <Group justify="space-between">
                                                            <Text
                                                                 size="md"
                                                                 fw={700}
                                                                 style={{
                                                                      color: "#22C55E",
                                                                 }}
                                                            >
                                                                 Total Amount
                                                                 Payable
                                                            </Text>
                                                            <Text
                                                                 size="lg"
                                                                 fw={700}
                                                                 style={{
                                                                      color: "#22C55E",
                                                                 }}
                                                            >
                                                                 {formatCurrency(
                                                                      pendingSubmissionData.amount +
                                                                           (pendingSubmissionData.amount *
                                                                                selectedLoanData.interestRate *
                                                                                pendingSubmissionData.duration) /
                                                                                100
                                                                 )}
                                                            </Text>
                                                       </Group>
                                                  </Stack>
                                             </Paper>

                                             <Paper
                                                  p="md"
                                                  radius="md"
                                                  style={{
                                                       background: "#FEF3C7",
                                                       border: "1px solid #FCD34D",
                                                  }}
                                             >
                                                  <Group gap="sm" mb="xs">
                                                       <ThemeIcon
                                                            size="md"
                                                            radius="sm"
                                                            style={{
                                                                 background:
                                                                      "#F59E0B",
                                                            }}
                                                       >
                                                            <IconAlertCircle
                                                                 size={18}
                                                            />
                                                       </ThemeIcon>
                                                       <Text
                                                            size="sm"
                                                            fw={600}
                                                            style={{
                                                                 color: "#92400E",
                                                            }}
                                                       >
                                                            Purpose
                                                       </Text>
                                                  </Group>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       style={{
                                                            color: "#78350F",
                                                       }}
                                                  >
                                                       {
                                                            pendingSubmissionData.purpose
                                                       }
                                                  </Text>
                                             </Paper>
                                        </Stack>
                                   </Paper>
                              </motion.div>

                              <motion.div
                                   initial={{ opacity: 0, y: 10 }}
                                   animate={{ opacity: 1, y: 0 }}
                                   transition={{ duration: 0.3, delay: 0.1 }}
                              >
                                   <Group justify="flex-end" gap="sm">
                                        <Button
                                             variant="outline"
                                             color="gray"
                                             onClick={() => {
                                                  setConfirmationModalOpened(
                                                       false
                                                  );
                                                  setPendingSubmissionData(
                                                       null
                                                  );
                                             }}
                                             disabled={isSubmitting}
                                             leftSection={<IconX size={18} />}
                                        >
                                             Cancel
                                        </Button>
                                        <Button
                                             onClick={
                                                  handleConfirmLoanApplication
                                             }
                                             loading={isSubmitting}
                                             leftSection={
                                                  <IconShieldCheck size={18} />
                                             }
                                             size="md"
                                             style={{
                                                  background:
                                                       "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                                             }}
                                        >
                                             Confirm & Submit
                                        </Button>
                                   </Group>
                              </motion.div>
                         </Stack>
                    )}
               </Modal>
          </Container>
     );
}
